/* global globalThis */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Button,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableBody,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  Tooltip,
  styled,
} from "@mui/material";

import {
  Download as DownloadIcon,
  Calendar as CalendarIcon,
} from "lucide-react";

import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import PageHeader from "../../components/common/PageHeader";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchSupervisorReporting,
  exportSupervisorReporting,
} from "../../api/reporting";
import { useLoading } from "../../context/LoadingContext";

import {
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";

const MainContent = styled("div")(() => ({
  flexGrow: 1,
  padding: 0,
  margin: 0,
  width: "100%",
  minHeight: "80vh",
  backgroundColor: "#F7F7FB",
  overflow: "hidden",
}));

function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const SECTIONS = {
  members: {
    description: "Search and manage member information",
  },
  providers: {
    title: "Providers",
    description: "Search and manage provider information",
  },
  pharmacies: {
    title: "Pharmacies",
    description: "Search and manage pharmacy information",
  },
  programs: {
    title: "Programs",
    description: "Search and manage program information",
  },
};
const BREADCRUMBS = [
  {
    label: "Reporting",
    path: "/reporting/supervisor-reporting",
  },
  {
    label: "Supervisor Reporting",
    path: "/reporting/supervisor-reporting",
  },
];

const mapResponse = (apiRes) => {
  if (!apiRes) return [];
  const rows = apiRes.content || apiRes;
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    userName: r.userName || "N/A",
    systemLogouts: r.systemLogouts ?? "N/A",
    memberOutreachOutcomes: r.memberOutreachOutcomes || null,
    providerOutreachOutcomes: r.providerOutreachOutcomes || null,
    pharmacyOutreachOutcomes: r.pharmacyOutreachOutcomes || null,
    programOutcomes: r.programOutcomes || null,
  }));
};

const aggregateOutcome = (obj) => {
  if (!obj || typeof obj !== "object") return -Infinity;
  return Object.values(obj)
    .filter((v) => typeof v === "number")
    .reduce((sum, v) => sum + v, 0);
};

const getSortedData = (data, sortConfig) => {
  const sorted = [...data];
  const { key, direction } = sortConfig;
  const compare = (a, b) => {
    const av = a[key];
    const bv = b[key];
    if (
      key === "memberOutreachOutcomes" ||
      key === "providerOutreachOutcomes" ||
      key === "pharmacyOutreachOutcomes" ||
      key === "programOutcomes"
    ) {
      return aggregateOutcome(av) - aggregateOutcome(bv);
    }
    const an = typeof av === "number" ? av : Number.parseFloat(av);
    const bn = typeof bv === "number" ? bv : Number.parseFloat(bv);
    const bothNumeric = !Number.isNaN(an) && !Number.isNaN(bn);
    if (bothNumeric) return an - bn;
    return String(av ?? "").localeCompare(String(bv ?? ""));
  };
  sorted.sort((a, b) => (direction === "asc" ? compare(a, b) : -compare(a, b)));
  return sorted;
};

const getSortArrows = () => (
  <span
    style={{ display: "inline-flex", flexDirection: "column", marginLeft: 4 }}
  >
    <ArrowDropUpIcon
      style={{
        fontSize: 16,
        color: "inherit",
        lineHeight: 0,
        opacity: 0.8,
      }}
    />
    <ArrowDropDownIcon
      style={{
        fontSize: 16,
        color: "inherit",
        marginTop: -8,
        lineHeight: 0,
        opacity: 0.8,
      }}
    />
  </span>
);

const getAriaSort = (sortConfig, key) => {
  if (sortConfig.key !== key) {
    return "none";
  }
  if (sortConfig.direction === "asc") {
    return "ascending";
  } else if (sortConfig.direction === "desc") {
    return "descending";
  } else {
    return "none";
  }
};

const DateRangeSelectionDialog = ({
  open,
  onClose,
  dateRange,
  onDateChange,
  endDateError,
  setEndDateError,
  onApply,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="date-range-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            padding: "16px",
            minWidth: 400,
          },
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          id="date-range-dialog-title"
          variant="h6"
          sx={{ color: "#3D3769", fontWeight: 600 }}
        >
          Select Date Range
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close date range dialog"
        >
          <CloseIcon sx={{ color: "#3D3769" }} />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            display="flex"
            gap={2}
            mb={3}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <Box flex={1}>
              <Typography
                variant="body2"
                sx={{
                  color: "#3D3769",
                  mb: 1,
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                Start Date
              </Typography>
              <DesktopDatePicker
                value={dateRange.startDate}
                disableFuture
                minDate={dateRange.startDate || undefined}
                onChange={(date) => {
                  onDateChange(date, "startDate");
                  setEndDateError(() => {
                    if (date && dateRange.endDate && dateRange.endDate < date) {
                      return "End date cannot be earlier than start date";
                    }
                    return "";
                  });
                }}
                slotProps={{
                  openPickerButton: {
                    "aria-label": "Open start date picker",
                  },
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "YYYY-MM-DD",
                    slotProps: {
                      input: {
                        sx: {
                          height: "40px",
                          "& input": {
                            padding: "8px 14px",
                            color: dateRange.startDate ? "#111827" : "#6B7280",
                            "&::placeholder": {
                              color: "#6B7280",
                              opacity: 1,
                            },
                          },
                        },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": { borderColor: "#E8E8E9" },
                        "&:hover fieldset": {
                          borderColor: "#D0D0D0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#E8E8E9",
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
            <Box flex={1}>
              <Typography
                variant="body2"
                sx={{
                  color: "#3D3769",
                  mb: 1,
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                End Date
              </Typography>
              <DesktopDatePicker
                value={dateRange.endDate}
                disableFuture
                onChange={(date) => {
                  onDateChange(date, "endDate");
                  if (
                    dateRange.startDate &&
                    date &&
                    date < dateRange.startDate
                  ) {
                    setEndDateError(
                      "End date cannot be earlier than start date",
                    );
                  } else {
                    setEndDateError("");
                  }
                }}
                slotProps={{
                  openPickerButton: {
                    "aria-label": "Open end date picker",
                  },
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "YYYY-MM-DD",
                    error: Boolean(endDateError),
                    helperText: endDateError || "",
                    slotProps: {
                      input: {
                        sx: {
                          height: "40px",
                          "& input": {
                            padding: "8px 14px",
                            color: dateRange.endDate ? "#111827" : "#6B7280",
                            "&::placeholder": {
                              color: "#6B7280",
                              opacity: 1,
                            },
                          },
                        },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": { borderColor: "#E8E8E9" },
                        "&:hover fieldset": {
                          borderColor: "#D0D0D0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#E8E8E9",
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </LocalizationProvider>
        <Box display="flex" justifyContent="flex-start" mt={2}>
          <Button
            variant="contained"
            onClick={onApply}
            disabled={
              !(dateRange.startDate && dateRange.endDate) ||
              Boolean(endDateError)
            }
            sx={{
              backgroundColor: "#3D3769",
              borderRadius: "24px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1,
              fontSize: "14px",
              "&:hover": {
                backgroundColor: "#322E59",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E8E8E9",
                color: "#A0A0A0",
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

DateRangeSelectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dateRange: PropTypes.object.isRequired,
  onDateChange: PropTypes.func.isRequired,
  endDateError: PropTypes.string,
  setEndDateError: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

const SupervisorReportingTable = ({
  loading,
  error,
  results,
  pageIndex,
  totalRows,
  computedTableHeight,
  sortedData,
  sortConfig,
  onSort,
  tableContainerRef,
}) => {
  if (error) {
    return (
      <p
        style={{
          color: "red",
          backgroundColor: "#ffffff",
          padding: "8px 12px",
          borderRadius: "4px",
          border: "1px solid #ffcccc",
          display: "inline-block",
        }}
      >
        {error}
      </p>
    );
  }

  if (results.length === 0 && (!loading || pageIndex !== 0)) {
    return null;
  }

  return (
    <div style={{ marginBottom: "16px", marginRight: "0px" }}>
      <TableContainer
        ref={tableContainerRef}
        tabIndex={0}
        component="section"
        aria-label="Supervisor reporting table"
        sx={{
          maxHeight: computedTableHeight || "calc(100dvh - 180px)",
          overflowY: "auto",
          overflowX: "scroll",
          height: computedTableHeight || "calc(100dvh - 180px)",
          backgroundColor: "#fff",
          scrollbarGutter: "stable",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            width: "100%",
            tableLayout: "auto",
            minWidth: 1600,
            "& .MuiTableCell-root": {
              color: "#403C6F",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "16px 24px",
              borderBottom: "none",
              "&:not(.MuiTableCell-head)": {
                fontWeight: 500,
                fontSize: "16px",
              },
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#CFCEDA",
              padding: "12px 20px !important",
              fontStyle: "normal",
              fontWeight: 550,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0px",
            },
            "& .MuiTableBody-root .MuiTableRow-root": {
              height: 90,
            },

            "& .MuiTableCell-root:nth-of-type(1)": {
              minWidth: 220,
            },
            "& .MuiTableCell-root:nth-of-type(2)": {
              minWidth: 180,
            },
            "& .MuiTableCell-root:nth-of-type(3)": {
              minWidth: 200,
            },
            "& .MuiTableCell-root:nth-of-type(4)": {
              minWidth: 300,
            },
            "& .MuiTableCell-root:nth-of-type(5)": {
              minWidth: 300,
            },
            "& .MuiTableCell-root:nth-of-type(6)": {
              minWidth: 270,
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#CFCEDA",
                "& .MuiTableCell-head": {
                  borderRight: "none !important",
                },
              }}
            >
              {[
                { key: "userName", label: "User Name" },
                { key: "systemLogouts", label: "System Logouts" },
                {
                  key: "memberOutreachOutcomes",
                  label: "Member Outreach Outcomes",
                },
                {
                  key: "providerOutreachOutcomes",
                  label: "Provider Outreach Outcomes",
                  width: 110,
                },
                {
                  key: "pharmacyOutreachOutcomes",
                  label: "Pharmacy Outreach Outcomes",
                  width: 20,
                  cellStyle: { maxWidth: 20, minWidth: 20 },
                },
                {
                  key: "programOutcomes",
                  label: "Program Outcomes",
                  width: 20,
                  cellStyle: { maxWidth: 20, minWidth: 20 },
                },
              ].map((col) => (
                <TableCell
                  key={col.key}
                  onClick={() => onSort(col.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSort(col.key);
                    }
                  }}
                  tabIndex={0}
                  aria-sort={getAriaSort(sortConfig, col.key)}
                  sx={{
                    cursor: "pointer",
                    color: "#403C6F",
                    fontWeight: 700,
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: col.width,
                    ...col.cellStyle,
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {col.label} {getSortArrows()}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((member) => (
              <TableRow
                key={member.userName}
                hover
                sx={{
                  "&:hover": {
                    backgroundColor: "#F9F9F9 !important",
                  },
                }}
              >
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    fontWeight: 500,
                  }}
                >
                  <Tooltip
                    title={member.userName || ""}
                    placement="bottom-start"
                    arrow
                    describeChild
                    disableInteractive
                    slotProps={{
                      popper: {
                        sx: {
                          "& .MuiTooltip-tooltip": {
                            backgroundColor: "#1c1c1f",
                            color: "#FFFFFF",
                            margin: 0,
                            fontSize: "12px",
                            lineHeight: 1.3,
                          },
                          "& .MuiTooltip-arrow": {
                            color: "#1c1c1f",
                            margin: 0,
                            left: "50% !important",
                            transform: "translateX(-50%) !important",
                          },
                        },
                      },
                      tooltip: {
                        sx: {
                          margin: 0,
                          marginTop: "0px !important",
                          padding: "4px 8px",
                        },
                      },
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: "17px",
                        lineHeight: "24px",
                        color: "#4b3f72",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                        maxWidth: "100%",
                      }}
                    >
                      {member.userName}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      minWidth: "120px",
                    }}
                  >
                    {member.systemLogouts}
                  </div>
                </TableCell>

                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: "500",
                    fontSize: "16px",
                  }}
                >
                  <OutcomeLines data={member.memberOutreachOutcomes} />
                </TableCell>

                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: 110,
                    fontWeight: "500",
                    fontSize: "16px",
                  }}
                >
                  <OutcomeLines data={member.providerOutreachOutcomes} />
                </TableCell>

                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: 110,
                    fontWeight: "500",
                    fontSize: "16px",
                  }}
                >
                  <OutcomeLines data={member.pharmacyOutreachOutcomes} />
                </TableCell>

                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: 110,
                    fontWeight: "500",
                    fontSize: "16px",
                  }}
                >
                  <OutcomeLines data={member.programOutcomes} type="program" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

const ExportDataButton = ({ totalRows, appliedDateRange, setError }) => {
  return (
    <Button
      startIcon={
        <DownloadIcon
          style={{
            width: "18px",
            height: "16px",
            color: totalRows > 0 ? "#403C6F" : "#999893",
          }}
        />
      }
      onClick={async () => {
        if (totalRows === 0) return;
        try {
          const today = new Date();
          const startOfYear = new Date(today.getFullYear(), 0, 1);
          const useCustom =
            appliedDateRange.startDate && appliedDateRange.endDate;
          const params = {
            startDate: format(
              useCustom ? appliedDateRange.startDate : startOfYear,
              "yyyy-MM-dd",
            ),
            endDate: format(
              useCustom ? appliedDateRange.endDate : today,
              "yyyy-MM-dd",
            ),
          };
          const blob = await exportSupervisorReporting(params);

          const url = globalThis.URL.createObjectURL(blob);
          const a = document.createElement("a");

          const formatMDY = (dateStr) => {
            const d = new Date(dateStr);
            return `${String(d.getMonth() + 1).padStart(2, "0")}_${String(
              d.getDate(),
            ).padStart(2, "0")}_${d.getFullYear()}`;
          };
          const rangeLabel = `(${formatMDY(params.startDate)})-(${formatMDY(
            params.endDate,
          )})`;
          a.href = url;
          a.download = `Supervisor_Reporting_${rangeLabel}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          globalThis.URL.revokeObjectURL(url);
        } catch (e) {
          console.error("Export failed", e);
          setError("Failed to export data");
        }
      }}
      disabled={totalRows === 0}
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "24px",
        letterSpacing: "0%",
        borderRadius: "20px",
        border: "1px solid #E8E8E9",
        textTransform: "none",
        backgroundColor: totalRows > 0 ? "#FFFFFF" : "#EFEFEF",
        color: totalRows > 0 ? "#403C6F" : "#999893",
        px: 2,
        py: 0,
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
      }}
    >
      Export Data
    </Button>
  );
};

ExportDataButton.propTypes = {
  totalRows: PropTypes.number.isRequired,
  appliedDateRange: PropTypes.object.isRequired,
  setError: PropTypes.func.isRequired,
};

const DateFilterControl = ({
  appliedDateRange,
  openDateDialog,
  closeDateDialog,
  setAppliedDateRange,
  setDateRange,
  setPageIndex,
  totalRows,
}) => {
  const hasDateRange = Boolean(
    appliedDateRange.startDate && appliedDateRange.endDate,
  );

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAppliedDateRange({
      startDate: null,
      endDate: null,
    });
    setDateRange({ startDate: null, endDate: null });
    setPageIndex(0);
    closeDateDialog();
  };

  const handleClick = (e) => {
    const clearIcon = e.target.closest('[data-clear-icon="true"]');
    if (hasDateRange && clearIcon) {
      handleClear(e);
      return;
    }
    openDateDialog();
  };

  const formatDate = (date) => new Date(date).toLocaleDateString("en-US");

  const labelText = hasDateRange
    ? `${formatDate(appliedDateRange.startDate)} - ${formatDate(
        appliedDateRange.endDate,
      )}`
    : "Select Date Range";

  const ariaLabel = hasDateRange
    ? `${labelText}, click X icon to clear`
    : labelText;

  const iconColor = totalRows > 0 ? "#403C6F" : "#999893";
  const iconStyle = { width: "18px", height: "16px", color: iconColor };

  const ClearButton = (
    <Box
      component="span"
      data-clear-icon="true"
      onClick={handleClear}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        ml: 0.5,
        color: iconColor,
        cursor: totalRows > 0 ? "pointer" : "not-allowed",
        opacity: totalRows > 0 ? 1 : 0.5,
        "&:hover": {
          opacity: totalRows > 0 ? 0.7 : 0.5,
        },
      }}
      aria-hidden="true"
    >
      <CloseIcon style={iconStyle} />
    </Box>
  );

  return (
    <Button
      variant="outlined"
      endIcon={hasDateRange ? ClearButton : <CalendarIcon style={iconStyle} />}
      onClick={handleClick}
      aria-label={ariaLabel}
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        border: "1px solid #E8E8E9",
        lineHeight: "24px",
        letterSpacing: "0%",
        borderRadius: "20px",
        textTransform: "none",
        color: "#403C6F",
        backgroundColor: "#FFFFFF",
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
      }}
    >
      {labelText}
    </Button>
  );
};

DateFilterControl.propTypes = {
  appliedDateRange: PropTypes.object.isRequired,
  openDateDialog: PropTypes.func.isRequired,
  closeDateDialog: PropTypes.func.isRequired,
  setAppliedDateRange: PropTypes.func.isRequired,
  setDateRange: PropTypes.func.isRequired,
  setPageIndex: PropTypes.func.isRequired,
  totalRows: PropTypes.number.isRequired,
};

SupervisorReportingTable.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  results: PropTypes.array.isRequired,
  pageIndex: PropTypes.number.isRequired,
  totalRows: PropTypes.number.isRequired,
  computedTableHeight: PropTypes.number.isRequired,
  sortedData: PropTypes.array.isRequired,
  sortConfig: PropTypes.object.isRequired,
  onSort: PropTypes.func.isRequired,
  tableContainerRef: PropTypes.object.isRequired,
};

const useDateRangeFilter = () => {
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [endDateError, setEndDateError] = useState("");
  const [dateDialogWasApplied, setDateDialogWasApplied] = useState(false);

  const openDateDialog = () => {
    setDateDialogOpen(true);
    if (!dateDialogWasApplied) {
      setDateRange({ startDate: null, endDate: null });
      setEndDateError("");
    }
  };

  const closeDateDialog = () => {
    setDateDialogOpen(false);
    setDateRange({ startDate: null, endDate: null });
    setEndDateError("");
    setDateDialogWasApplied(false);
  };

  const handleDateChange = (date, type) => {
    setDateRange((prev) => ({ ...prev, [type]: date }));
  };

  const applyDateRange = () => {
    if (dateRange.startDate && dateRange.endDate && !endDateError) {
      setAppliedDateRange({ ...dateRange });
      setDateDialogWasApplied(true);
      setDateDialogOpen(false);
      return true;
    }
    return false;
  };

  const clearDateRange = () => {
    setAppliedDateRange({ startDate: null, endDate: null });
    setDateRange({ startDate: null, endDate: null });
  };

  return {
    dateRange,
    appliedDateRange,
    dateDialogOpen,
    endDateError,
    setEndDateError,
    openDateDialog,
    closeDateDialog,
    handleDateChange,
    applyDateRange,
    clearDateRange,
    setAppliedDateRange,
    setDateRange,
  };
};

const useSupervisorData = (appliedDateRange, pageSize = 30) => {
  const [supervisorReporting, setSupervisorReporting] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFetchingMoreRef = useRef(false);
  const lastFetchSignatureRef = useRef(null);
  const { showLoading, hideLoading } = useLoading();

  const loadInitialData = useCallback(async () => {
    try {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const useCustom = appliedDateRange.startDate && appliedDateRange.endDate;
      const effectiveStart = useCustom
        ? appliedDateRange.startDate
        : startOfYear;
      const effectiveEnd = useCustom ? appliedDateRange.endDate : today;

      const params = {
        startDate: format(effectiveStart, "yyyy-MM-dd"),
        endDate: format(effectiveEnd, "yyyy-MM-dd"),
        page: pageIndex,
        size: pageSize,
      };
      setLoading(true);

      const signature = JSON.stringify({
        ...params,
        startDate: format(effectiveStart, "yyyy-MM-dd"),
        endDate: format(effectiveEnd, "yyyy-MM-dd"),
      });

      if (lastFetchSignatureRef.current === signature) {
        setLoading(false);
        return;
      }
      lastFetchSignatureRef.current = signature;

      showLoading();
      const apiRes = await fetchSupervisorReporting(params);
      setError(null);
      const mapped = mapResponse(apiRes);

      if (pageIndex === 0) {
        setResults(mapped);
        setSupervisorReporting(mapped);
      } else {
        setResults((prev) => [...prev, ...mapped]);
        setSupervisorReporting((prev) => [...prev, ...mapped]);
      }
      setTotalRows(apiRes.totalElements || mapped.length);
    } catch (err) {
      console.error(err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
      isFetchingMoreRef.current = false;
      hideLoading();
    }
  }, [
    pageIndex,
    pageSize,
    appliedDateRange.startDate,
    appliedDateRange.endDate,
    showLoading,
    hideLoading,
  ]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    setPageIndex(0);
    setResults([]);
    setSupervisorReporting([]);
    lastFetchSignatureRef.current = null;
  }, [appliedDateRange]);

  return {
    results,
    loading,
    error,
    setError,
    totalRows,
    pageIndex,
    setPageIndex,
    supervisorReporting,
    isFetchingMoreRef,
  };
};

const useTableSort = (data) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const cycleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      if (prev.direction === "desc") return { key: null, direction: null };
      return { key, direction: "asc" };
    });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return data;
    return getSortedData(data, sortConfig);
  }, [data, sortConfig]);

  return { sortConfig, cycleSort, sortedData };
};

const useTableLayout = (
  loading,
  isFetchingMoreRef,
  results,
  totalRows,
  setPageIndex,
) => {
  const tableContainerRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const [computedTableHeight, setComputedTableHeight] = useState(0);
  const [containerScale, setContainerScale] = useState(1);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || isFetchingMoreRef.current) return;
      const canLoadMore = results.length < totalRows;
      if (!canLoadMore) return;

      const threshold = 100;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        isFetchingMoreRef.current = true;
        setPageIndex((prev) => prev + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, results.length, totalRows, setPageIndex, isFetchingMoreRef]);

  // Recompute available table height on layout/viewport changes
  useEffect(() => {
    const computeHeight = () => {
      const el = resultsContainerRef.current;
      if (!el) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const top = el.getBoundingClientRect().top;

      const isSmallLaptop =
        window.innerWidth <= 1440 && window.innerHeight <= 900;
      const extra = isSmallLaptop ? 24 : 0;
      const bottomPadding = isSmallLaptop ? 4 : 16;
      const h = Math.max(200, Math.floor(vh - top - bottomPadding + extra));
      setComputedTableHeight(h);
    };

    computeHeight();
    window.addEventListener("resize", computeHeight);
    const ro = new ResizeObserver(() => computeHeight());
    try {
      ro.observe(document.body);
    } catch (error_) {
      console.log("Unexpected error", error_);
    }
    return () => {
      window.removeEventListener("resize", computeHeight);
      ro.disconnect?.();
    };
  }, []);

  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);

  return {
    tableContainerRef,
    resultsContainerRef,
    computedTableHeight,
    containerScale,
  };
};

const SupervisorReporting = () => {
  const {
    dateRange,
    appliedDateRange,
    dateDialogOpen,
    endDateError,
    setEndDateError,
    openDateDialog,
    closeDateDialog,
    handleDateChange,
    applyDateRange,

    setAppliedDateRange,
    setDateRange,
  } = useDateRangeFilter();

  const {
    results,
    loading,
    error,
    setError,
    totalRows,
    pageIndex,
    setPageIndex,
    supervisorReporting,
    isFetchingMoreRef,
  } = useSupervisorData(appliedDateRange);

  const { sortConfig, cycleSort, sortedData } =
    useTableSort(supervisorReporting);

  const {
    tableContainerRef,
    resultsContainerRef,
    computedTableHeight,
    containerScale,
  } = useTableLayout(
    loading,
    isFetchingMoreRef,
    results,
    totalRows,
    setPageIndex,
  );

  const location = useLocation();
  const isPriorityOutreach = location.pathname.includes("priority-outreach");
  const isMemberMonitoring = location.pathname.includes("monitoring");

  const getCurrentSection = () => {
    if (isPriorityOutreach) return "priority-outreach";
    if (isMemberMonitoring) return "monitoring";
    return "members";
  };
  const [activeSection] = useState(getCurrentSection());
  const currentSection = activeSection ? SECTIONS[activeSection] : null;
  const sectionTitle = currentSection?.title || "";
  const sectionDescription = currentSection?.description || "";

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: "transparent",
      }}
    >
      <CssBaseline />

      <MainContent className="search-page">
        {dateDialogOpen && (
          <Box
            onClick={closeDateDialog}
            sx={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.35)",
              zIndex: 1200,
            }}
          />
        )}
        <div className="search-page-container">
          <div
            className="search-page-content"
            style={{
              transform: `scale(${containerScale})`,
              transformOrigin: "top left",
              width: containerScale < 1 ? "111%" : "100%",
              minHeight: "92vh",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minHeight: "55px",
                ml: 0,
                mt: 1,
                marginBottom: "-8px",
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: "30px",
                letterSpacing: "0%",
                color: "#080A0E",
                "& a, & span:last-child": {
                  color: "#080A0E",
                  fontSize: "20px",
                  fontWeight: 600,
                  lineHeight: "30px",
                  letterSpacing: "0%",
                  textDecoration: "none",
                },
                "& a:hover": {
                  textDecoration: "underline",
                },
                "& .breadcrumb-separator": {
                  margin: "0 4px",
                  color: "#080A0E",
                },
              }}
            >
              {BREADCRUMBS.map((breadcrumb, index) => (
                <React.Fragment
                  key={`breadcrumb-${breadcrumb.label || ""}-${breadcrumb.path || ""}-${index}`}
                >
                  {index > 0 && (
                    <span className="breadcrumb-separator">&gt;</span>
                  )}
                  {breadcrumb.path ? (
                    <a href={breadcrumb.path}>{breadcrumb.label}</a>
                  ) : (
                    <span>{breadcrumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <PageHeader
                  title={sectionTitle}
                  description={sectionDescription}
                />
              </Box>
              <Stack direction="row" spacing={1} marginRight={1}>
                <ExportDataButton
                  totalRows={totalRows}
                  appliedDateRange={appliedDateRange}
                  setError={setError}
                />
                <DateFilterControl
                  appliedDateRange={appliedDateRange}
                  openDateDialog={openDateDialog}
                  closeDateDialog={closeDateDialog}
                  setAppliedDateRange={setAppliedDateRange}
                  setDateRange={setDateRange}
                  setPageIndex={setPageIndex}
                  totalRows={totalRows}
                />
                <DateRangeSelectionDialog
                  open={dateDialogOpen}
                  onClose={closeDateDialog}
                  dateRange={dateRange}
                  onDateChange={handleDateChange}
                  endDateError={endDateError}
                  setEndDateError={setEndDateError}
                  onApply={() => {
                    if (applyDateRange()) {
                      setPageIndex(0);
                    }
                  }}
                />
              </Stack>
            </Box>

            <div className="search-results-container" ref={resultsContainerRef}>
              <SupervisorReportingTable
                loading={loading}
                error={error}
                results={results}
                pageIndex={pageIndex}
                totalRows={totalRows}
                computedTableHeight={computedTableHeight}
                sortedData={sortedData}
                sortConfig={sortConfig}
                onSort={cycleSort}
                tableContainerRef={tableContainerRef}
              />
            </div>
          </div>
        </div>
      </MainContent>
    </Box>
  );
};

const OutcomeLines = ({ data, type }) => {
  if (!data) return <span style={{ color: "#403C6F" }}>N/A</span>;
  if (typeof data !== "object")
    return <span style={{ color: "#403C6F" }}>{String(data)}</span>;
  const entries = Object.entries(data).filter(
    ([, v]) => v !== null && v !== undefined,
  );
  if (entries.length === 0)
    return <span style={{ color: "#403C6F" }}>N/A</span>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {entries.map(([label, value]) => (
        <Box
          key={label}
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: "20px",
            color: "#403C6F",
            whiteSpace: "normal",
          }}
        >
          {type === "program"
            ? `${value} Programs ${label
                .toLowerCase()
                .charAt(0)
                .toUpperCase()}${label.toLowerCase().slice(1)}`
            : `${value} ${label}`}
        </Box>
      ))}
    </Box>
  );
};

OutcomeLines.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  type: PropTypes.string,
};

export default SupervisorReporting;
